'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * MatrixRain — Klassischer digitaler Regen (Matrix-Stil)
 *
 * Technik: Canvas 2D, 30fps throttle, semi-transparentes Overlay für Trail-Effekt
 * CPU-Impact: Low (~80 Spalten, einfache Zeichen-Operationen)
 *
 * Verwendung:
 *   <MatrixRainBackground>
 *     <div className="relative z-10">Content</div>
 *   </MatrixRainBackground>
 */

const CHARS =
  'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン' +
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ@#$%&*+=-<>';

function MatrixRainBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  fontSize = 14,
  variant = 'auto',
  ...props
}) {
  const isDark = useBackgroundTheme(variant);
  const bgClass = isDark ? 'bg-slate-950' : 'bg-white';
  const canvasRef = React.useRef(null);
  const animRef = React.useRef(null);
  const lastFrameRef = React.useRef(0);
  const pausedRef = React.useRef(false);
  const columnsRef = React.useRef([]);
  const frameCountRef = React.useRef(0);
  const FPS = 30;
  const INTERVAL = 1000 / FPS;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });

    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    canvas.width = w;
    canvas.height = h;

    const initCols = (width, height) => {
      const count = Math.floor(width / fontSize);
      // Stagger start: mix of above-screen and mid-screen positions
      columnsRef.current = Array.from({ length: count }, () =>
        Math.floor(Math.random() * (height / fontSize + 10)) - 10,
      );
      // Initial background fill
      ctx.fillStyle = isDark ? 'rgb(2,6,23)' : 'rgb(255,255,255)';
      ctx.fillRect(0, 0, width, height);
    };

    initCols(w, h);

    const observer = new IntersectionObserver(
      ([entry]) => { pausedRef.current = !entry.isIntersecting; },
      { threshold: 0.01 },
    );
    observer.observe(canvas);

    const ro = new ResizeObserver(() => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w;
      canvas.height = h;
      initCols(w, h);
    });
    ro.observe(canvas);

    const draw = (timestamp) => {
      animRef.current = requestAnimationFrame(draw);
      if (pausedRef.current) return;
      if (timestamp - lastFrameRef.current < INTERVAL) return;
      lastFrameRef.current = timestamp;

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      frameCountRef.current++;

      // Fade trail: semi-transparent overlay each frame
      ctx.fillStyle = isDark
        ? `rgba(2,6,23,0.06)`
        : `rgba(255,255,255,0.07)`;
      ctx.fillRect(0, 0, w, h);

      ctx.font = `${fontSize}px monospace`;

      // Advance columns every N frames based on speed
      const stepEvery = Math.max(1, Math.round(2 / speed));
      const doStep = frameCountRef.current % stepEvery === 0;

      const cols = columnsRef.current;
      for (let i = 0; i < cols.length; i++) {
        const y = cols[i];
        const xPx = i * fontSize;
        const yPx = y * fontSize;

        // Draw head character — brightest point
        const headChar = CHARS[Math.floor(Math.random() * CHARS.length)];
        if (isDark) {
          // Bright white-green head, green-glow body
          ctx.fillStyle = `rgba(200,255,200,${0.85 * opacity})`;
        } else {
          ctx.fillStyle = `rgba(0,90,50,${0.3 * opacity})`;
        }
        ctx.fillText(headChar, xPx, yPx);

        // One step behind: main rain color
        if (y > 0) {
          const bodyChar = CHARS[Math.floor(Math.random() * CHARS.length)];
          if (isDark) {
            ctx.fillStyle = `rgba(0,210,80,${0.55 * opacity})`;
          } else {
            ctx.fillStyle = `rgba(0,110,55,${0.18 * opacity})`;
          }
          ctx.fillText(bodyChar, xPx, (y - 1) * fontSize);
        }

        // Advance column position
        if (doStep) {
          if (yPx > h + fontSize * 5 && Math.random() > 0.97) {
            // Reset above screen with a random delay
            cols[i] = Math.floor(Math.random() * -20);
          } else {
            cols[i]++;
          }
        }
      }
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      observer.disconnect();
      ro.disconnect();
    };
  }, [speed, fontSize, opacity, isDark]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      data-slot="matrix-rain-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      {...props}
    >
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />
      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

export { MatrixRainBackground };
